import React from "react";

/* ------------------------------------------------------------------
   Loading / empty / stat building blocks.

   <Spinner>     — the activity indicator (also what LoadingSpinner renders).
   <EmptyState>  — "nothing here yet" panel with an optional action.
   <StatTile>    — a KPI number with a label, for dashboard summary rows.
------------------------------------------------------------------ */

/** iOS-style activity indicator: twelve fading spokes. */
export const Spinner = ({ size = 28, label = "Loading", className = "" }) => (
  <span
    role="status"
    aria-label={label}
    className={`relative inline-block text-[color:var(--app-text-muted)] ${className}`}
    style={{ width: size, height: size }}
  >
    {Array.from({ length: 12 }).map((_, i) => (
      <span
        key={i}
        aria-hidden="true"
        className="absolute left-1/2 top-0 rounded-full bg-current"
        style={{
          width: Math.max(2, size / 11),
          height: size * 0.28,
          marginLeft: -Math.max(2, size / 11) / 2,
          transformOrigin: `50% ${size / 2}px`,
          transform: `rotate(${i * 30}deg)`,
          animation: "app-spinner-fade 1s linear infinite",
          animationDelay: `${(i - 12) / 12}s`,
        }}
      />
    ))}
    <style>{`@keyframes app-spinner-fade{0%{opacity:.85}100%{opacity:.12}}`}</style>
  </span>
);

/** Centered spinner block with optional caption, for a loading page/section. */
export const LoadingState = ({ label = "Loading…", className = "" }) => (
  <div
    className={`flex w-full flex-col items-center justify-center gap-3 py-16 ${className}`}
  >
    <Spinner />
    {label && <p className="app-muted text-sm">{label}</p>}
  </div>
);

/**
 *   <EmptyState icon={InboxIcon} title="No requests yet"
 *     description="New service requests show up here."
 *     action={<Button variant="contained">Create one</Button>} />
 */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
  className = "",
}) => (
  <div
    className={`flex flex-col items-center justify-center text-center ${
      compact ? "px-4 py-8" : "px-6 py-14"
    } ${className}`}
  >
    {Icon && (
      <span className="app-icon-tile mb-4 h-14 w-14 rounded-2xl bg-slate-500/10 text-slate-500 dark:text-slate-400">
        <Icon fontSize="medium" />
      </span>
    )}
    {title && <p className="text-base font-semibold">{title}</p>}
    {description && (
      <p className="app-muted mt-1 max-w-sm text-sm leading-relaxed">
        {description}
      </p>
    )}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

const STAT_TONES = {
  default: "",
  sky: "text-sky-600 dark:text-sky-300",
  emerald: "text-emerald-600 dark:text-emerald-300",
  amber: "text-amber-600 dark:text-amber-300",
  rose: "text-rose-600 dark:text-rose-300",
  violet: "text-violet-600 dark:text-violet-300",
};

/** KPI tile. `onClick` makes it a button (e.g. to filter by that status). */
export const StatTile = ({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
  active = false,
  onClick,
  className = "",
}) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={`app-card flex min-w-0 flex-col rounded-2xl border p-4 text-left ${
        onClick
          ? "transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
          : ""
      } ${active ? "ring-2 ring-sky-500" : ""} ${className}`}
    >
      <span className="app-muted flex items-center gap-1.5 text-xs font-medium">
        {Icon && <Icon sx={{ fontSize: 16 }} />}
        {label}
      </span>
      <span
        className={`mt-1 text-2xl font-bold tabular-nums tracking-tight ${
          STAT_TONES[tone] || ""
        }`}
      >
        {value}
      </span>
      {hint && <span className="app-muted mt-0.5 text-xs">{hint}</span>}
    </Tag>
  );
};
